/**
 * Security-alert review receipts — per-USER (KAN-661/662, RD-SEC-046/047).
 * Replaces the company-wide `security_event_reviews` watermark: an admin
 * "Mark as reviewed" writes one receipt per alert shown, so each admin's list
 * is their own. Same adapter shape as `../delivery/deliveryIssueReceipts.js`:
 * per-user localStorage map, write-through via `upsertNoticeReceipts`,
 * monotonic merge-from-server, no shared `:anon` bucket.
 *
 * notice_key = `security_alert:<alertKey>`; alertKey = `<kind>:<audit row
 * uuid of the alert's first source event>` — ids + timestamps ONLY, never an
 * email, name or IP. `announced_at` is the moment THIS user reviewed it.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from './noticeReceiptMath';
import { tenantIdForNoticeWrite } from './noticeReceiptsSession';

const SECURITY_ALERT_PREFIX = 'security_alert:';
const MAX_KEY_CHARS = 200;
// Alerts only exist inside the 30-day lookback, so a receipt older than that
// can never match again — prune by age, then cap by count (newest kept).
const RECEIPT_MAX_AGE_MS = 45 * 24 * 60 * 60 * 1000;
export const SECURITY_ALERT_MAX_ENTRIES = 500;

export const SECURITY_ALERT_RECEIPTS_PREFIX = 'cvx-security-alert-reviewed-v1:';

export function securityAlertReceiptsKey(userId) {
  return `${SECURITY_ALERT_RECEIPTS_PREFIX}${userId}`;
}

/** The server notice_key for an alert key. Null when it would not fit. */
export function securityAlertNoticeKey(alertKey) {
  if (typeof alertKey !== 'string' || !alertKey) return null;
  const key = `${SECURITY_ALERT_PREFIX}${alertKey}`;
  return key.length <= MAX_KEY_CHARS ? key : null;
}

function capNewest(map, nowMs) {
  const ids = Object.keys(map).filter((id) => nowMs - Date.parse(map[id]) <= RECEIPT_MAX_AGE_MS);
  const keep = ids
    .sort((a, b) => Date.parse(map[b]) - Date.parse(map[a]))
    .slice(0, SECURITY_ALERT_MAX_ENTRIES);
  const out = {};
  keep.forEach((id) => { out[id] = map[id]; });
  return out;
}

/** `{ alertKey: isoInstant }`. No user id → nothing, ever. */
export function readSecurityAlertReviewed(userId, storage, nowMs = Date.now()) {
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(securityAlertReceiptsKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    Object.keys(parsed).forEach((id) => {
      const at = Date.parse(parsed[id]);
      if (!id || Number.isNaN(at)) return;
      out[id] = new Date(at).toISOString();
    });
    return capNewest(out, nowMs);
  } catch {
    return {};
  }
}

function writeMap(userId, next, storage, nowMs = Date.now()) {
  if (!userId) return;
  try {
    (storage || window.localStorage).setItem(
      securityAlertReceiptsKey(userId),
      JSON.stringify(capNewest(next, nowMs)),
    );
  } catch {
    /* best-effort local cache */
  }
}

/** Newest review time in the map, or null. */
export function newestSecurityAlertReview(map) {
  return Object.keys(map || {}).reduce((best, id) => maxIso(best, map[id]), null);
}

/**
 * Marks every alert key reviewed by `userId` (idempotent; max, never `=`) and
 * writes through to the server. Returns the RESULTING map.
 */
export function markSecurityAlertsReviewed(userId, alertKeys, storage, nowMs = Date.now(), opts = {}) {
  const current = readSecurityAlertReviewed(userId, storage, nowMs);
  if (!userId || !Array.isArray(alertKeys) || alertKeys.length === 0) return current;
  const stamp = new Date(nowMs).toISOString();
  const next = { ...current };
  const keys = alertKeys.filter((k) => securityAlertNoticeKey(k));
  keys.forEach((k) => { next[k] = maxIso(next[k], stamp); });
  writeMap(userId, next, storage, nowMs);
  const tenantId = tenantIdForNoticeWrite(userId);
  if (!opts.skipSync && tenantId && keys.length > 0) {
    upsertNoticeReceipts({
      tenantId,
      userId,
      rows: keys.map((k) => ({ notice_key: securityAlertNoticeKey(k), announced_at: next[k] })),
    });
  }
  return next;
}

export function ownsSecurityAlertNoticeKey(noticeKey) {
  return typeof noticeKey === 'string' && noticeKey.startsWith(SECURITY_ALERT_PREFIX);
}

/** Server rows → local map, MONOTONIC. */
export function mergeSecurityAlertReceiptsFromServer(userId, rows, storage, nowMs = Date.now()) {
  if (!userId || !Array.isArray(rows)) return;
  let merged = readSecurityAlertReviewed(userId, storage, nowMs);
  let changed = false;
  rows.forEach((row) => {
    if (!ownsSecurityAlertNoticeKey(row?.notice_key) || !row.announced_at) return;
    const key = row.notice_key.slice(SECURITY_ALERT_PREFIX.length);
    if (!key || !isNewerIso(row.announced_at, merged[key])) return;
    merged = { ...merged, [key]: row.announced_at };
    changed = true;
  });
  if (changed) writeMap(userId, merged, storage, nowMs);
}

/** Snapshot for the one-time post-hydration upload. */
export function collectSecurityAlertReceiptsForUpload(userId, storage) {
  if (!userId) return [];
  const map = readSecurityAlertReviewed(userId, storage, Date.now());
  return Object.keys(map)
    .map((k) => ({ notice_key: securityAlertNoticeKey(k), announced_at: map[k] }))
    .filter((r) => r.notice_key);
}
